import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Search, Plus, ExternalLink, Calendar, Globe, RefreshCw, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { useToast } from "@/lib/toast";
import { Badge, PageHeader, Empty, Spinner } from "@/components/UI";

const META = {
  egp:        { label: "e-GP Uganda",       subtitle: "Government of Uganda procurement portal · authenticated" },
  ungm:       { label: "UNGM",              subtitle: "UN Global Marketplace" },
  "ungm-eoi": { label: "UNGM · My EOI",     subtitle: "UNGM tenders you've expressed interest in · authenticated" },
  unhcr:      { label: "UNHCR",             subtitle: "United Nations Refugee Agency" },
  nsd:        { label: "PAU / NSD",         subtitle: "Petroleum Authority of Uganda — Supplier database" },
};

export default function Feed() {
  const { source } = useParams();
  const meta = META[source] || { label: source, subtitle: "" };
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const nav = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [feedMeta, setFeedMeta] = useState({ live: false, last_refreshed: null });
  const { toast } = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await api.feed(source);
      setItems(d.items || []);
      setFeedMeta({ live: d.live, last_refreshed: d.last_refreshed });
    } catch { toast("Failed to load feed", "error"); }
    finally { setLoading(false); }
  }, [source, toast]);

  useEffect(() => { load(); }, [load]);

  const refresh = async () => {
    setRefreshing(true);
    try {
      const r = await api.refreshFeed(source);
      if (r.status === "queued") {
        toast(
          "Authenticated scrape queued — this takes ~2 minutes. Refresh the page shortly to see new results.",
          "success"
        );
      } else {
        toast(`Refreshed — ${r.count} live items`, "success");
      }
      // Reload cache (for queued scrapes, this shows the *previous* result;
      // user can pull again after ~2 minutes)
      load();
    } catch (e) {
      toast(e?.response?.data?.detail || "Refresh failed", "error");
    } finally { setRefreshing(false); }
  };

  const filtered = items.filter((it) =>
    !search || JSON.stringify(it).toLowerCase().includes(search.toLowerCase())
  );

  const quickAdd = async (it) => {
    try {
      await api.createBid({
        title: it.bid_details || it.title || "Opportunity",
        client_name: it.entity_name || it.organization || "",
        ref_number: it.ref_number || "",
        deadline: it.deadline || "",
        scope_of_work: it.bid_details || "",
        status: "Bidding",
        source,
        currency: "UGX",
      });
      toast("Added to My Bids", "success");
    } catch {
      toast("Failed to add bid", "error");
    }
  };

  return (
    <div data-testid={`page-feed-${source}`}>
      <PageHeader
        icon={Globe}
        title={meta.label}
        subtitle={
          <>
            {meta.subtitle}{" "}
            <span className="ml-1 inline-block">
              {feedMeta.live ? (
                <Badge tone="emerald" dot>
                  Live · {feedMeta.last_refreshed ? new Date(feedMeta.last_refreshed).toLocaleString() : "just now"}
                </Badge>
              ) : (
                <Badge tone="amber">{source === "egp" ? "Sample data — login required" : "Sample data — click Refresh"}</Badge>
              )}
            </span>
          </>
        }
        action={
          <button
            data-testid="refresh-feed"
            onClick={refresh}
            disabled={refreshing}
            className="btn btn-primary"
          >
            <RefreshCw size={14} className={refreshing ? "animate-spin" : ""} />
            {refreshing ? "Fetching…" : "Refresh now"}
          </button>
        }
      />

      <div className="card card-tight mb-5">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            data-testid="feed-search"
            placeholder="Search opportunities…"
            className="input pl-9"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {loading ? (
        <div className="muted text-sm flex items-center gap-2"><Spinner /> Loading…</div>
      ) : filtered.length === 0 ? (
        <Empty icon={Globe} title="No opportunities" description="Adjust your search or check back later." />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {filtered.map((it) => (
            <div key={it.id} data-testid={`feed-${it.id}`} className="card card-padded">
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="min-w-0">
                  <div className="eyebrow" style={{ color: "var(--green-700)" }}>{it.ref_number}</div>
                  <h3 className="font-bold text-[15px] mt-1 leading-snug">{it.bid_details || it.title}</h3>
                  <div className="text-xs muted mt-1">{it.entity_name || it.organization}</div>
                </div>
                <div className="flex flex-col items-end gap-1">
                  {it.is_new && <Badge tone="green" dot>NEW</Badge>}
                  {it.authenticated && <Badge tone="emerald">AUTHED</Badge>}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2 text-[12px] muted mb-4">
                {it.deadline && (
                  <div className="flex items-center gap-1.5">
                    <Calendar size={11} /> <span>Deadline: <b style={{ color: "var(--ink-900)" }}>{it.deadline}</b></span>
                  </div>
                )}
                {it.procurement_method && <div>{it.procurement_method}</div>}
                {it.notice_type && <div>{it.notice_type}</div>}
                {it.bid_document_fee && <div>Doc fee: {it.bid_document_fee}</div>}
                {it.bid_security_amount && <div>Security: {it.bid_security_amount}</div>}
                {it.category && <div>Category: {it.category}</div>}
              </div>

              <div className="flex items-center gap-2">
                <button
                  data-testid={`add-${it.id}`}
                  onClick={() => quickAdd(it)}
                  className="btn btn-primary btn-sm"
                >
                  <Plus size={12} /> Add to My Bids
                </button>
                <button
                  data-testid={`ai-${it.id}`}
                  title="Send this notice to the AI Bid Wizard for full extraction, then attach Vault certificates."
                  onClick={() => {
                    const seed = it.detail_url
                      ? `url=${encodeURIComponent(it.detail_url)}&auto=1`
                      : `text=${encodeURIComponent(
                          `${it.ref_number || ""}\n${it.bid_details || it.title || ""}\n${it.entity_name || it.organization || ""}\nDeadline: ${it.deadline || "N/A"}`.trim()
                        )}&auto=1`;
                    const hint = encodeURIComponent(
                      `${meta.label} · ${it.category || "ICT"} · ${it.entity_name || ""}`.trim()
                    );
                    nav(`/bid-wizard?${seed}&hint=${hint}`);
                  }}
                  className="btn btn-ghost btn-sm"
                  style={{ color: "var(--green-700)" }}
                >
                  <Sparkles size={12} /> Analyse with AI
                </button>
                {it.detail_url && (
                  <a
                    href={it.detail_url}
                    target="_blank"
                    rel="noreferrer"
                    className="btn btn-ghost btn-sm"
                  >
                    <ExternalLink size={12} /> View details
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
